import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { getPortalTransportStudents, updatePortalTransportTimes } from "../../lib/api";

function AddressLine({ label, name, value }) {
  if (!value && !name) return null;
  return (
    <div className="text-sm">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {name ? <span className="ml-1 font-medium">({name})</span> : null}
      <p className="text-slate-700">{value || "—"}</p>
    </div>
  );
}

function StudentCard({ s, onSaved, updateFn }) {
  const [pickup, setPickup] = useState(s.pickup_time || "");
  const [dropoff, setDropoff] = useState(s.dropoff_time || "");
  const [saving, setSaving] = useState(false);
  const dirty = pickup !== (s.pickup_time || "") || dropoff !== (s.dropoff_time || "");

  const save = async () => {
    setSaving(true);
    try {
      await updateFn(s.id, { pickup_time: pickup, dropoff_time: dropoff });
      toast.success("Times saved");
      onSaved(s.id, { pickup_time: pickup, dropoff_time: dropoff });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid={`transport-student-${s.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="font-semibold text-[#00305E]" data-testid={`transport-student-name-${s.id}`}>{s.name}</p>
            <p className="text-xs text-muted-foreground">
              {s.grade ? `Grade ${s.grade}` : ""}{s.student_number ? ` · #${s.student_number}` : ""}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-md bg-slate-50 border p-3">
          <AddressLine label="Home" value={s.home_address} />
          <AddressLine label="Transport" name={s.transport_name} value={s.transport_address} />
          <AddressLine label="Other" name={s.other_address_name} value={s.other_address} />
          {!s.home_address && !s.transport_address && !s.other_address && (
            <p className="text-sm text-muted-foreground sm:col-span-3">No address on file.</p>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Pickup Time</label>
            <Input type="time" value={pickup} onChange={(e) => setPickup(e.target.value)} className="w-36" data-testid={`transport-pickup-${s.id}`} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Dropoff Time</label>
            <Input type="time" value={dropoff} onChange={(e) => setDropoff(e.target.value)} className="w-36" data-testid={`transport-dropoff-${s.id}`} />
          </div>
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid={`transport-save-${s.id}`}>
            {saving ? "Saving…" : "Save Times"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const DEFAULT_INTRO =
  "These are the students assigned to your transportation service. You can update each student's " +
  "pickup and dropoff times. Addresses are shown for your reference. You'll receive an email when " +
  "one of these students is marked absent or is leaving early.";

export default function PortalTransport({
  fetchFn = getPortalTransportStudents,
  updateFn = updatePortalTransportTimes,
  intro = DEFAULT_INTRO,
  emptyText = "No students assigned to your service yet.",
}) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchFn();
      setStudents(res.data || []);
    } catch {
      toast.error("Failed to load students");
    } finally { setLoading(false); }
  }, [fetchFn]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (id, patch) =>
    setStudents((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const filtered = students.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="portal-transport">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
        {intro}
      </div>
      <Input placeholder="Search students…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="transport-search" />

      {loading ? (
        <p className="text-sm text-muted-foreground py-6">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6" data-testid="transport-empty">{emptyText}</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.map((s) => <StudentCard key={s.id} s={s} onSaved={onSaved} updateFn={updateFn} />)}
        </div>
      )}
    </div>
  );
}
